import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { usePageTitle } from '../../components/shell/AppShell';
import { useAccessToken } from '../../auth/useAccessToken';
import { decodeSas, useMyPermissions, useMyPreferences, SAS_ANALYZER_PERMISSION } from '../../api/queries';
import type { ApiError } from '../../api/client';
import type { SasDecodeResult } from '../../api/types';
import { CONSOLE_HANDOFF_QUERY_PARAM, consumeConsoleCapture } from '../../utils/consoleHandoff';
import { buildExportFilename } from '../../utils/export';
import { buildExchangeRanges, buildLineModel, exportLineModelText } from './lineModel';
import { resolveSasAnalyzerColors } from './colorPreferences';
import { buildCommandSearchIndex, wrapIndex } from './commandSearch';
import type { SasCommandSearchOption } from './commandSearch';
import DecodeStream from './DecodeStream';
import ColorsDialog from './ColorsDialog';

const SAMPLE = `21:15:44.123 10 TX 01 1C
21:15:44.180 11 RX 01 1C 00 00 12 34 00 00 09 87 00 00 50 00 00 00 00 00 00 00 00 42 00 00 00 40 00 00 00 03 00 00 00 01 6D 83
21:15:45.001 12 TX 80
21:15:45.040 13 RX 00`;

// Matches SasDecodeOptions.MaxBytes in Route.Sas.Decode/SasDecoder.cs — the
// decode API's own input cap. Rejected client-side (with a visible message)
// rather than silently truncating a "Load file" capture the way the API
// truncates an over-long paste.
const MAX_LOAD_FILE_BYTES = 262144;

// AppShell's outer shell (`AppShell.tsx`) sets `minHeight: '100vh'` on its
// root flex box — a FLOOR, not a fixed height — and never resets `html` /
// `body` to `height: 100%` (no global stylesheet does either). That breaks
// percentage-height resolution down the tree: once this page's content
// (paste box + toolbars + a long decode stream) needs more than one
// viewport's worth of space, `height: '100%'` on this page's root Stack
// doesn't resolve to anything definite, so the ENTIRE document falls back
// to native scrolling instead of just the decode stream below — carrying
// the search toolbar and prev/next controls off-screen with it (the bug
// this file fixes). Anchoring directly off `100vh` sidesteps that broken
// ancestor chain: this Stack always gets a real, bounded height, so the
// `flex: 1, minHeight: 0, overflowY: 'auto'` decode-stream box (the only
// thing meant to scroll) is the only thing that ever does.
//   65  = AppShell's AppBar: Toolbar `minHeight: 64` + 1px `borderBottom`
//         (AppShell.tsx: `borderBottom: 1` on the `<AppBar>` ~L389, and
//         `minHeight: 64` on the `<Toolbar>` ~L391).
//   48  = AppShell's content-pane `p: 3` (24px top + 24px bottom) — the
//         `<Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>` at ~L490.
// Keep in sync with `AppShell.tsx` if either of those change — grep for
// the line refs above if this drifts.
const APP_SHELL_CHROME_PX = 65 + 48;

// jsdom's Blob/File polyfill has no `.text()` (confirmed against the
// version this repo tests against) and real browsers have supported
// FileReader far longer than File.text() — read via FileReader so this
// works identically in both.
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsText(file);
  });
}

export default function SasAnalyzerPage() {
  usePageTitle('Machine Comm Analyser');
  const getToken = useAccessToken();
  const perms = useMyPermissions();
  const allowed = perms.data?.codes?.includes(SAS_ANALYZER_PERMISSION) ?? false;

  const [paste, setPaste] = useState('');
  const [result, setResult] = useState<SasDecodeResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);
  // Non-error notice for the console→analyser handoff (Task 4) when the
  // one-shot stash is missing/expired — the page still loads normally.
  const [handoffNotice, setHandoffNotice] = useState<string | null>(null);

  // Command-search dropdown + prev/next nav (Task 6): which command is
  // selected and which of its occurrences (0-based) is the current match.
  // Both reset whenever a fresh decode result lands — see the effect below.
  const [selectedCommand, setSelectedCommand] = useState<SasCommandSearchOption | null>(null);
  const [matchPos, setMatchPos] = useState(0);
  // Bumped on every prev/next step (including a wrap-around back to the
  // same single match) so DecodeStream re-scrolls even when the target
  // range's value didn't change.
  const [scrollToken, setScrollToken] = useState(0);
  // MUI Autocomplete's open state, controlled so the toolbar's Enter/
  // Shift+Enter nav shortcut can tell "the popup is open, let it handle
  // this keypress" apart from "popup closed, Enter means next/prev".
  const [commandListOpen, setCommandListOpen] = useState(false);

  // "Load file" toolbar button: hidden <input type="file"> triggered by a
  // normal MUI Button (house style — no native dialogs beyond the OS
  // picker itself, which IS the point here).
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileNotice, setFileNotice] = useState<{ severity: 'warning' | 'error'; message: string } | null>(null);

  // Per-user colour palette (Task 3) — server-persisted on
  // auth.app_user.preferences, loaded on mount and applied to the decode
  // stream via CSS variables. Falls back to the Option-C defaults for any
  // role not (yet) saved.
  const prefsQ = useMyPreferences();
  const colors = useMemo(() => resolveSasAnalyzerColors(prefsQ.data), [prefsQ.data]);

  // Accepts an optional override so the console-handoff mount effect (below)
  // can decode the just-consumed capture immediately without waiting on a
  // `setPaste` re-render — the Decode button itself always calls `decode()`
  // (no args) and falls back to the current `paste` state.
  const decode = useCallback(async (text: string = paste) => {
    setBusy(true);
    setError(null);
    try {
      setResult(await decodeSas(text, getToken));
    } catch (e) {
      setError(e as ApiError);
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, [paste, getToken]);

  // "Load file": read a saved console-capture .txt from disk (OS picker via
  // the hidden <input>), fill the paste box, and decode it immediately —
  // same override-arg path the console handoff uses. Guardrails per the
  // errors-visible house rule: an empty file or one over the decode API's
  // own input cap gets a visible notice instead of a crash or a silent
  // server-side truncation; a read failure surfaces too.
  const handleFileSelected = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Reset immediately (not just on success) so picking the SAME file
    // again still fires a change event — browsers dedupe <input type=file>
    // changes when the value/FileList is unchanged from last time.
    e.target.value = '';
    if (!file) return;

    setFileNotice(null);
    setHandoffNotice(null);

    if (file.size === 0) {
      setFileNotice({ severity: 'warning', message: `"${file.name}" is empty — nothing to load.` });
      return;
    }
    if (file.size > MAX_LOAD_FILE_BYTES) {
      setFileNotice({
        severity: 'error',
        message: `"${file.name}" is ${Math.ceil(file.size / 1024).toLocaleString()} KB — the decoder accepts `
          + `at most ${(MAX_LOAD_FILE_BYTES / 1024).toLocaleString()} KB. Trim the capture and try again.`,
      });
      return;
    }

    try {
      const text = await readFileAsText(file);
      setPaste(text);
      void decode(text);
    } catch {
      setFileNotice({
        severity: 'error',
        message: `Could not read "${file.name}" — try again or paste the capture manually.`,
      });
    }
  }, [decode]);

  // "Clear": resets the whole analyser to a fresh state in one click — paste
  // box, decode result/error, command-search selection (also covered by the
  // reset-on-new-result effect below, once `result` flips to null), and any
  // handoff/load-file notices. The Colours palette is a persisted user
  // preference, not analysis state — deliberately untouched. Non-destructive
  // (the source capture still exists wherever it came from), so per the
  // no-native-dialogs house rule this fires immediately, no confirm prompt.
  const clearAll = useCallback(() => {
    setPaste('');
    setResult(null);
    setError(null);
    setHandoffNotice(null);
    setFileNotice(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  // Guards the handoff-consume effect below against React.StrictMode's
  // double-invoke of mount effects (main.tsx wraps the app in
  // <React.StrictMode>). Consuming a one-shot stash is deliberately
  // non-idempotent — the second read is SUPPOSED to come back empty — so
  // without this sentinel the second StrictMode pass would see
  // "already consumed" and show the spurious expired/used notice right on
  // top of an otherwise-successful decode. A genuine remount gets a fresh
  // ref (and a fresh `key` on the query string, since the first pass strips
  // it), so this only suppresses the StrictMode replay, not real re-visits.
  const consumedHandoffRef = useRef(false);

  // Console → analyser handoff (Task 4): on first mount, if the console
  // page opened this tab with `?consoleCapture=<key>`, consume the one-shot
  // localStorage stash, fill the paste box, and decode immediately. The
  // query flag is stripped from the URL either way (success or
  // missing/expired) so a reload/back-nav never re-attempts the same
  // already-spent key. A missing/expired key is NOT an error — it's a
  // normal empty page plus a visible notice; the console session itself is
  // never touched by any of this (pure localStorage + this tab's state).
  useEffect(() => {
    if (consumedHandoffRef.current) return;
    consumedHandoffRef.current = true;

    const key = searchParams.get(CONSOLE_HANDOFF_QUERY_PARAM);
    if (!key) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CONSOLE_HANDOFF_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
    const captured = consumeConsoleCapture(key);
    if (captured === null) {
      setHandoffNotice(
        'The console capture link has expired or was already used — paste the capture manually.',
      );
      return;
    }
    setPaste(captured);
    void decode(captured);
    // Run once on mount only — consuming a one-shot localStorage stash
    // must never repeat on a later re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The single line-model the decode stream renders AND the .txt exporter
  // serialises — one code path, so the screen and the export can never diverge.
  const lineModel = useMemo(() => (result ? buildLineModel(result) : []), [result]);
  // Per-exchange model-line ranges (lineModel.ts) and the command-search
  // dropdown's option list (commandSearch.ts) — both pure derivations of
  // `result`, recomputed together whenever a fresh decode lands.
  const exchangeRanges = useMemo(() => (result ? buildExchangeRanges(result) : []), [result]);
  const commandOptions = useMemo(() => (result ? buildCommandSearchIndex(result) : []), [result]);

  // Reset command-search selection/position whenever a fresh decode result
  // lands (including a decode that produced zero exchanges) — a leftover
  // selection would otherwise reference exchange indexes from the PREVIOUS
  // result, which `exchangeRanges` no longer agrees with.
  useEffect(() => {
    setSelectedCommand(null);
    setMatchPos(0);
  }, [result]);

  // The model-line range DecodeStream highlights/scrolls to for the current
  // match, or null when nothing is selected.
  const highlightRange = useMemo(() => {
    if (!selectedCommand) return null;
    const exchangeIndex = selectedCommand.exchangeIndexes[matchPos];
    return exchangeRanges[exchangeIndex] ?? null;
  }, [selectedCommand, matchPos, exchangeRanges]);

  const goToMatch = useCallback((direction: 1 | -1) => {
    if (!selectedCommand || selectedCommand.exchangeIndexes.length === 0) return;
    setMatchPos((p) => wrapIndex(p, selectedCommand.exchangeIndexes.length, direction));
    setScrollToken((t) => t + 1);
  }, [selectedCommand]);

  const decodedText = useCallback((): string => exportLineModelText(lineModel), [lineModel]);

  const copyDecoded = useCallback(() => {
    void navigator.clipboard.writeText(decodedText());
  }, [decodedText]);

  const downloadDecoded = useCallback(() => {
    const blob = new Blob([decodedText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = buildExportFilename('sas-analyzer', 'decoded', 'txt');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [decodedText]);

  if (!perms.isLoading && !allowed) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        You don't have the <code>{SAS_ANALYZER_PERMISSION}</code> permission required to use the Machine Comm Analyser.
      </Alert>
    );
  }

  const positionLabel = selectedCommand
    ? `${selectedCommand.exchangeIndexes.length === 0 ? 0 : matchPos + 1} of ${selectedCommand.exchangeIndexes.length}`
    : null;
  const nothingToClear = paste.length === 0 && !result;

  return (
    // usePageTitle() above already publishes this page's name into the
    // AppShell topbar (AppShell.tsx renders it as the sticky AppBar's <h6>)
    // — an in-page heading here would just repeat it, so this screen goes
    // straight to the toolbar/work area. `height: calc(100vh - …)` (see
    // APP_SHELL_CHROME_PX above) + `minHeight: 0` let the results section
    // (below) flex-grow to fill the remaining viewport height instead of
    // leaving dead space under a short paste box; when there's no flex-grow
    // child yet (no decode run) this has no visible effect on the compact
    // top section.
    <Stack spacing={2} sx={{ p: 2, maxWidth: 1100, height: `calc(100vh - ${APP_SHELL_CHROME_PX}px)`, minHeight: 0 }}>
      {/* Containment for the "controls always win" contract: THIS Box (not
          the TextField/textarea itself) is the part that shrinks on a short
          viewport. MUI's TextareaAutosize sets the textarea's own height
          directly from its measured scrollHeight (driven by minRows/
          maxRows), ignoring whatever size flexbox allocates it — a bare
          `flexShrink` on the TextField alone would let an over-tall
          textarea paint straight OVER the Decode/Load toolbar row below it
          once the flex column runs out of room, since nothing in the chain
          clips it. Putting `overflow: 'auto'` on this wrapping Box instead
          means the textarea still renders at its full computed height, but
          that overflow is clipped/scrolled inside the Box's own bounds —
          it can never bleed into a sibling. `minHeight: 64` is a floor
          (~3 visible rows) so the paste area never collapses to something
          unusable under heavy squeeze. */}
      <Box sx={{ flexShrink: 1, minHeight: 64, overflow: 'auto' }}>
        <TextField
          label="Raw SAS hex (one frame per line)"
          multiline
          minRows={6}
          maxRows={16}
          fullWidth
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder={SAMPLE}
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
        />
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
        <Button variant="contained" onClick={() => decode()} disabled={busy || !paste.trim()}>
          {busy ? <CircularProgress size={18} /> : 'Decode'}
        </Button>
        <Button onClick={() => setPaste(SAMPLE)}>Load sample</Button>
        <Button onClick={() => fileInputRef.current?.click()}>Load file</Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,text/plain"
          hidden
          data-testid="sas-load-file-input"
          onChange={(e) => void handleFileSelected(e)}
        />
        <Button onClick={() => setColorsOpen(true)}>Colours</Button>
        <Button onClick={clearAll} disabled={nothingToClear}>Clear</Button>
        {result && <Button onClick={copyDecoded}>Copy decoded</Button>}
        {result && <Button onClick={downloadDecoded}>Download decoded</Button>}
      </Stack>

      {handoffNotice && (
        <Alert severity="info" onClose={() => setHandoffNotice(null)}>
          {handoffNotice}
        </Alert>
      )}

      {fileNotice && (
        <Alert severity={fileNotice.severity} onClose={() => setFileNotice(null)}>
          {fileNotice.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          {error.status ? `HTTP ${error.status} — ` : ''}
          {error.message ?? 'Decode failed'}
        </Alert>
      )}

      {result && (
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          {result.truncated && (
            <Alert severity="warning" variant="outlined">
              Input truncated — {result.warnings.join('; ')}
            </Alert>
          )}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            data-testid="sas-command-search-toolbar"
            // Fixed upper zone: this row (search + prev/next + position)
            // must always stay on screen, so it never shrinks and — being a
            // sibling of the scrollable decode-stream Box below, not a
            // descendant of it — it never scrolls out of view either.
            sx={{ flexShrink: 0 }}
          >
            <Autocomplete<SasCommandSearchOption>
              open={commandListOpen}
              onOpen={() => setCommandListOpen(true)}
              onClose={() => setCommandListOpen(false)}
              options={commandOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              value={selectedCommand}
              onChange={(_e, value) => {
                setSelectedCommand(value);
                setMatchPos(0);
                setScrollToken((t) => t + 1);
              }}
              disabled={commandOptions.length === 0}
              size="small"
              data-testid="sas-command-search"
              sx={{ minWidth: 320, flexGrow: 1, maxWidth: 480 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search commands"
                  placeholder={commandOptions.length === 0 ? 'No commands in this decode' : 'Search commands…'}
                  onKeyDown={(e) => {
                    // Let the open popup handle its own Enter (select the
                    // highlighted option) / arrow-key navigation — only
                    // treat Enter as prev/next nav while it's closed.
                    if (commandListOpen) return;
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      goToMatch(e.shiftKey ? -1 : 1);
                    }
                  }}
                />
              )}
            />
            <IconButton
              aria-label="Previous command match"
              size="small"
              onClick={() => goToMatch(-1)}
              disabled={!selectedCommand || selectedCommand.exchangeIndexes.length === 0}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Next command match"
              size="small"
              onClick={() => goToMatch(1)}
              disabled={!selectedCommand || selectedCommand.exchangeIndexes.length === 0}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
            {positionLabel && (
              <Typography variant="body2" color="text.secondary" data-testid="sas-command-search-position">
                {positionLabel}
              </Typography>
            )}
          </Stack>

          <Box data-testid="sas-decode-scroll-region" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <DecodeStream lines={lineModel} colors={colors} highlightRange={highlightRange} scrollToken={scrollToken} />
          </Box>
        </Stack>
      )}

      <ColorsDialog open={colorsOpen} onClose={() => setColorsOpen(false)} colors={colors} />
    </Stack>
  );
}
